import React, { useState, useMemo } from 'react';
import DateField from '../Shared/DateField';
import { FilterPill } from '../Shared/FilterPill';
import './Tracker.css';

/* ── Taxe EUIPO opozitii (EUR, Anexa I RMUE) ── */
const TAXE_DEFAULT = {
  opozitie:        320,    // art. 46 alin. (3) RMUE
  apel:            720,    // art. 68 RMUE
  anulare:         630,    // decadere/nulitate, art. 63 alin. (2)
  continuare:      400,    // continuarea procedurii
  restitutio:      200,    // repunere in drepturi
  plafon_opozitie: 300,    // plafon cheltuieli de reprezentare, opozitie
  plafon_apel:     550,    // plafon cheltuieli de reprezentare, apel
};

const TERMENE_DEFAULT = {
  opozitie_luni:         3,  // de la publicare (MUE)
  ir_decalaj_luni:       1,  // IR cu desemnarea UE: termenul incepe la o luna dupa republicare
  cooling_off_luni:      2,  // prelungibil pana la 24 luni
  cooling_off_max:      24,
  substantiere_luni:     2,  // probe oponent
  observatii_luni:       2,  // observatiile solicitantului
  dovada_luni:           2,  // dovada folosirii efective
  apel_declaratie_luni:  2,  // declaratia de recurs
  apel_motive_luni:      4,  // memoriul de motivare
};

const TEMEIURI = [
  ['8_1_a',  'Identitate de semne si de produse — art. 8 alin. (1) lit. (a)'],
  ['8_1_b',  'Risc de confuzie — art. 8 alin. (1) lit. (b)'],
  ['8_3',    'Cerere depusa de agent fara consimtamant — art. 8 alin. (3)'],
  ['8_4',    'Semn neînregistrat folosit in comert — art. 8 alin. (4)'],
  ['8_4a',   'Denumire de origine sau indicatie geografica — art. 8 alin. (4a)'],
  ['8_5',    'Marca de renume — art. 8 alin. (5)'],
  ['8_6',    'Cerere de rea-credinta a agentului / alte drepturi anterioare'],
];

const TIP_DREPT = [
  ['mue',      'Marca a Uniunii Europene'],
  ['nationala','Marca nationala'],
  ['ir_ue',    'Inregistrare internationala cu desemnarea UE'],
  ['ir_nat',   'Inregistrare internationala cu desemnare nationala'],
  ['notorie',  'Marca notorie (art. 6 bis Conventia de la Paris)'],
  ['neinreg',  'Semn neînregistrat folosit in comert'],
  ['ig',       'Denumire de origine / indicatie geografica'],
];

const SOLUTII = [
  ['',                 '— nesolutionata —'],
  ['respinsa',         'Opozitie respinsa — cererea continua'],
  ['admisa_partial',   'Opozitie admisa partial'],
  ['admisa_total',     'Opozitie admisa in totalitate — cerere respinsa'],
  ['retrasa',          'Opozitie retrasa'],
  ['acord',            'Stinsa prin acord intre parti'],
  ['suspendata',       'Procedura suspendata'],
  ['inchisa_retragere','Inchisa prin retragerea cererii'],
];

const STRATEGII = [
  ['',           '— nestabilita —'],
  ['aparare',    'Aparare pe fond'],
  ['dovada',     'Solicitare dovada a folosirii'],
  ['limitare',   'Limitarea listei de produse/servicii'],
  ['divizare',   'Divizarea cererii'],
  ['negociere',  'Negociere / acord de coexistenta'],
  ['conversie',  'Conversia in cereri nationale'],
  ['retragere',  'Retragerea cererii'],
];

/* ── Helpers ── */
const todayMid = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const parseISO = (s) => (s ? new Date(s + 'T00:00:00') : null);
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };
const fmtRO = (d) => d ? d.toLocaleDateString('ro-RO', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—';
const daysLeft = (d) => d ? Math.ceil((d - todayMid()) / 86400000) : null;
const fmtEur = (n) => Math.round(Number(n)).toLocaleString('ro-RO') + ' €';
/* termenul real din calendarul Oficiului are prioritate fata de cel calculat */
const pick = (manual, calculat) => parseISO(manual) || calculat;

let _nextRightId = 0;
const newRightId = () => `dr_${Date.now()}_${_nextRightId++}`;

export function emptyRecord() {
  return {
    directie: 'primita',           // primita | formulata
    tipCerere: 'mue',              // mue | ir_ue
    marca: '',
    numarCerere: '',               // 018xxxxxx
    clientul: '',
    claseMarca: '',
    dataPublicare: '',
    numarOpozitie: '',             // B xxx xxx
    dataOpozitie: '',
    dataAdmisibilitate: '',        // data comunicarii calendarului EUIPO
    oponent: '',
    mandatarOponent: '',
    claseOpuse: '',
    temeiuri: [],
    drepturi: [],                  // [{ id, tip, semn, numar, data, clase }]
    // cooling-off
    coolingStart: '',
    coolingPrelungit: false,
    coolingLuniTotal: 2,
    coolingOptOut: false,
    // faza contradictorie — termene din calendarul Oficiului
    termenSubstantiereManual: '',
    substantiereDepusa: false,
    termenObservatiiManual: '',
    observatiiDepuse: false,
    dovadaCeruta: false,
    dataCerereDovada: '',
    termenDovadaManual: '',
    dovadaPrimita: false,
    prelungiri: 0,
    suspendata: false,
    strategie: '',
    solutie: '',
    dataDecizie: '',
    cheltuieliInSarcina: '',
    apel: false,
    dataApel: '',
    transmisClient: false,
    observatii: '',
  };
}

/* ── Fee calc ── */
function calcTaxe(r, T) {
  const opozitie = T.opozitie;
  const apel = r.apel ? T.apel : 0;
  const propriile = (r.directie === 'formulata' ? opozitie : 0) + apel;
  const expunere = T.plafon_opozitie + (r.directie === 'primita' ? T.opozitie : 0);
  return { opozitie, apel, propriile, expunere };
}

/* ── Status ── */
function deriveStatus(r, TM) {
  const pub = parseISO(r.dataPublicare);
  const op = parseISO(r.dataOpozitie);
  const adm = parseISO(r.dataAdmisibilitate);
  const cool = parseISO(r.coolingStart);
  const cerDov = parseISO(r.dataCerereDovada);
  const dec = parseISO(r.dataDecizie);

  const opStart = pub ? (r.tipCerere === 'ir_ue' ? addMonths(pub, TM.ir_decalaj_luni) : pub) : null;
  const termenOpozitie = opStart ? addMonths(opStart, TM.opozitie_luni) : null;
  const coolingEnd = cool ? addMonths(cool, Number(r.coolingLuniTotal) || TM.cooling_off_luni) : null;
  const coolingActiv = coolingEnd && !r.coolingOptOut && todayMid() < coolingEnd;
  const coolingMax = cool ? addMonths(cool, TM.cooling_off_max) : null;

  const bazaSubstantiere = coolingEnd || adm;
  const termenSubstantiere = pick(r.termenSubstantiereManual, bazaSubstantiere ? addMonths(bazaSubstantiere, TM.substantiere_luni) : null);
  const termenObservatii = pick(r.termenObservatiiManual, termenSubstantiere ? addMonths(termenSubstantiere, TM.observatii_luni) : null);
  const termenDovada = pick(r.termenDovadaManual, cerDov ? addMonths(cerDov, TM.dovada_luni) : null);
  const termenApelDeclaratie = dec ? addMonths(dec, TM.apel_declaratie_luni) : null;
  const termenApelMotive = dec ? addMonths(dec, TM.apel_motive_luni) : null;

  const steps = [
    { key:'publicare',    label:'Publicare cerere',       done: !!pub,                    date: pub },
    { key:'opozitie',     label:'Opozitie depusa',        done: !!op,                     date: op },
    { key:'admisibil',    label:'Admisibilitate',         done: !!adm,                    date: adm },
    { key:'cooling',      label:'Cooling-off',            done: !!coolingEnd && !coolingActiv, date: coolingEnd },
    { key:'probe',        label:'Probe oponent',          done: r.substantiereDepusa,     date: termenSubstantiere },
    { key:'observatii',   label:'Observatii solicitant',  done: r.observatiiDepuse,       date: termenObservatii },
    { key:'decizie',      label:'Decizie',                done: !!dec,                    date: dec },
    { key:'apel',         label:'Termen apel',            done: !!dec && (r.apel || (termenApelDeclaratie ? todayMid() > termenApelDeclaratie : false)), date: termenApelDeclaratie },
    { key:'client',       label:'Informat clientul',      done: r.transmisClient,         date: null },
  ];

  let alert;
  if (r.suspendata) {
    alert = { label:'Procedura suspendata', sev:'neutral' };
  } else if (!op) {
    if (termenOpozitie && todayMid() < termenOpozitie)
      alert = { label:'Termen de opozitie in curs — monitorizare', date: termenOpozitie, sev:'watch' };
    else
      alert = { label:'Opozitie de inregistrat in dosar', sev:'neutral' };
  } else if (!adm) {
    alert = { label:'Asteptare verificare de admisibilitate', sev:'info' };
  } else if (coolingActiv) {
    alert = { label: r.coolingPrelungit ? 'Cooling-off prelungit — negociere' : 'Cooling-off — negociere intre parti', date: coolingEnd, sev:'watch' };
  } else if (r.dovadaCeruta && !r.dovadaPrimita) {
    alert = { label:'Termen pentru dovada folosirii efective', date: termenDovada, sev:'act' };
  } else if (!r.substantiereDepusa) {
    alert = {
      label: r.directie === 'formulata' ? 'De completat probele si argumentele' : 'Termen pentru probele oponentului',
      date: termenSubstantiere,
      sev: r.directie === 'formulata' ? 'act' : 'watch',
    };
  } else if (!r.observatiiDepuse) {
    alert = {
      label: r.directie === 'primita' ? 'De depus observatiile solicitantului' : 'Termen pentru observatiile partii adverse',
      date: termenObservatii,
      sev: r.directie === 'primita' ? 'act' : 'watch',
    };
  } else if (!dec) {
    alert = { label:'In pronuntare la divizia de opozitie', sev:'info' };
  } else if (!r.apel && termenApelDeclaratie && todayMid() <= termenApelDeclaratie) {
    alert = { label:'Termen pentru declaratia de recurs', date: termenApelDeclaratie, sev:'act' };
  } else if (r.apel && termenApelMotive && todayMid() <= termenApelMotive) {
    alert = { label:'Termen pentru memoriul de motivare a recursului', date: termenApelMotive, sev:'act' };
  } else if (!r.transmisClient) {
    alert = { label:'De informat clientul despre solutie', sev:'act' };
  } else {
    alert = { label:'Dosar inchis', sev:'done' };
  }

  if (alert.date) {
    const dl = daysLeft(alert.date);
    alert.daysLeft = dl;
    if (dl < 0 && alert.sev === 'act') alert.sev = 'overdue';
  }

  return { steps, alert, opStart, termenOpozitie, coolingEnd, coolingActiv, coolingMax, termenSubstantiere, termenObservatii, termenDovada, termenApelDeclaratie, termenApelMotive };
}

/* ════════════════════════ Component ════════════════════════ */
export default function OpozitieEUIPOTracker({ data, onChange }) {
  const [r, setR] = useState(data);
  const [taxe, setTaxe] = useState(TAXE_DEFAULT);
  const [termene, setTermene] = useState(TERMENE_DEFAULT);
  const [showSetari, setShowSetari] = useState(false);

  const set = (patch) => {
    const next = { ...r, ...patch };
    setR(next);
    onChange(next);
  };

  const taxeCalc = useMemo(() => calcTaxe(r, taxe), [r, taxe]);
  const status = useMemo(() => deriveStatus(r, termene), [r, termene]);

  const toggleTemei = (k) => {
    const t = r.temeiuri.includes(k) ? r.temeiuri.filter(x => x !== k) : [...r.temeiuri, k];
    set({ temeiuri: t });
  };

  const addDrept = () => set({ drepturi: [...(r.drepturi || []), { id: newRightId(), tip:'mue', semn:'', numar:'', data:'', clase:'' }] });
  const setDrept = (id, patch) => set({ drepturi: r.drepturi.map(d => d.id === id ? { ...d, ...patch } : d) });
  const delDrept = (id) => set({ drepturi: r.drepturi.filter(d => d.id !== id) });

  return (
    <div className="tracker">
      <AlertBanner status={status} />
      <Timeline steps={status.steps} />

      {/* Identificare */}
      <TrackerCard title="Marca vizata">
        <div className="field">
          <label>Pozitia in procedura</label>
          <SegControl value={r.directie} onChange={v => set({ directie: v })}
            options={[['primita','Opozitie primita'],['formulata','Opozitie formulata']]} />
        </div>
        <div className="field">
          <label>Tipul cererii atacate</label>
          <SegControl value={r.tipCerere} onChange={v => set({ tipCerere: v })}
            options={[['mue','Cerere MUE'],['ir_ue','IR cu desemnarea UE']]} />
          <span className="f-hint">La IR cu desemnarea UE, termenul de opozitie incepe la o luna de la republicare.</span>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Denumirea marcii</label>
            <input type="text" value={r.marca} onChange={e => set({ marca: e.target.value })} />
          </div>
          <div className="field">
            <label>Nr. cerere</label>
            <input type="text" value={r.numarCerere} onChange={e => set({ numarCerere: e.target.value })} placeholder="018123456" className="mono" />
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Clientul</label>
            <input type="text" value={r.clientul} onChange={e => set({ clientul: e.target.value })} />
          </div>
          <div className="field">
            <label>Clasele cererii</label>
            <input type="text" value={r.claseMarca} onChange={e => set({ claseMarca: e.target.value })} placeholder="ex. 25, 35" className="mono" />
          </div>
        </div>
        <div className="field">
          <label>Data publicarii in Jurnalul Oficial EUIPO</label>
          <DateField value={r.dataPublicare} onChange={v => set({ dataPublicare: v })} />
          {status.opStart && r.tipCerere === 'ir_ue' && (
            <span className="f-hint">Inceput termen opozitie: {fmtRO(status.opStart)}</span>
          )}
          {status.termenOpozitie && (
            <span className="f-hint">Sfarsit termen opozitie: {fmtRO(status.termenOpozitie)} ({daysLeft(status.termenOpozitie)} zile)</span>
          )}
        </div>
      </TrackerCard>

      {/* Opozitia */}
      <TrackerCard title="Opozitia">
        <div className="f-row">
          <div className="field">
            <label>Nr. opozitie</label>
            <input type="text" value={r.numarOpozitie} onChange={e => set({ numarOpozitie: e.target.value })} placeholder="B 3 123 456" className="mono" />
          </div>
          <div className="field">
            <label>Data depunerii</label>
            <DateField value={r.dataOpozitie} onChange={v => set({ dataOpozitie: v })} />
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Oponent</label>
            <input type="text" value={r.oponent} onChange={e => set({ oponent: e.target.value })} />
          </div>
          <div className="field">
            <label>Mandatarul oponentului</label>
            <input type="text" value={r.mandatarOponent} onChange={e => set({ mandatarOponent: e.target.value })} />
          </div>
        </div>
        <div className="f-row">
          <div className="field">
            <label>Clasele vizate de opozitie</label>
            <input type="text" value={r.claseOpuse} onChange={e => set({ claseOpuse: e.target.value })} className="mono" />
          </div>
          <div className="field">
            <label>Notificarea de admisibilitate</label>
            <DateField value={r.dataAdmisibilitate} onChange={v => set({ dataAdmisibilitate: v })} />
            <span className="f-hint">Data comunicarii calendarului procedural.</span>
          </div>
        </div>

        <div className="tr-divider">Temeiuri invocate</div>
        <div style={{ display:'flex', flexDirection:'column', gap: 4 }}>
          {TEMEIURI.map(([k, l]) => (
            <label key={k} className="check-label">
              <input type="checkbox" checked={r.temeiuri.includes(k)} onChange={() => toggleTemei(k)} />
              {l}
            </label>
          ))}
        </div>
      </TrackerCard>

      {/* Drepturi anterioare */}
      <TrackerCard title="Drepturi anterioare invocate">
        <p className="f-hint mb8">Opoziția se poate intemeia pe mai multe drepturi anterioare.</p>
        {(r.drepturi || []).map(d => {
          const dd = parseISO(d.data);
          const vechi = dd ? (todayMid() - dd) / 86400000 > 5 * 365.25 : false;
          return (
            <div key={d.id} className="tr-card" style={{ marginBottom: 8 }}>
              <div className="tr-card-body">
                <div className="f-row">
                  <div className="field">
                    <label>Tip drept anterior</label>
                    <FilterPill value={d.tip} onChange={e => setDrept(d.id, { tip: e.target.value })}>
                      {TIP_DREPT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </FilterPill>
                  </div>
                  <div className="field">
                    <label>Semnul anterior</label>
                    <input type="text" value={d.semn} onChange={e => setDrept(d.id, { semn: e.target.value })} />
                  </div>
                </div>
                <div className="f-row">
                  <div className="field">
                    <label>Nr. inregistrare</label>
                    <input type="text" value={d.numar} onChange={e => setDrept(d.id, { numar: e.target.value })} className="mono" />
                  </div>
                  <div className="field">
                    <label>Clase</label>
                    <input type="text" value={d.clase} onChange={e => setDrept(d.id, { clase: e.target.value })} className="mono" />
                  </div>
                  <div className="field">
                    <label>Data inregistrarii</label>
                    <DateField value={d.data} onChange={v => setDrept(d.id, { data: v })} />
                  </div>
                </div>
                {vechi && <span className="f-hint" style={{ color:'var(--warn)' }}>Peste 5 ani — se poate cere dovada folosirii</span>}
                <button type="button" className="btn-link" style={{ color:'var(--danger)' }} onClick={() => delDrept(d.id)}>Sterge drept</button>
              </div>
            </div>
          );
        })}
        <button type="button" className="btn-link mt8" onClick={addDrept}>+ Adauga drept anterior</button>
      </TrackerCard>

      {/* Cooling-off */}
      <TrackerCard title="Cooling-off">
        <p className="f-hint mb8">Perioada initiala este de {termene.cooling_off_luni} luni, prelungibila o singura data pana la maximum {termene.cooling_off_max} de luni total.</p>
        <div className="f-row">
          <div className="field">
            <label>Data de inceput</label>
            <DateField value={r.coolingStart} onChange={v => set({ coolingStart: v })} />
          </div>
          <div className="field">
            <label>Durata totala (luni)</label>
            <input type="number" min="1" max={termene.cooling_off_max} value={r.coolingLuniTotal}
              onChange={e => set({ coolingLuniTotal: Math.min(termene.cooling_off_max, Math.max(1, Number(e.target.value)||1)) })} className="mono" />
          </div>
        </div>
        <label className="check-label">
          <input type="checkbox" checked={r.coolingPrelungit} onChange={e => set({ coolingPrelungit: e.target.checked })} />
          Prelungit la cererea comuna a partilor
        </label>
        <label className="check-label">
          <input type="checkbox" checked={r.coolingOptOut} onChange={e => set({ coolingOptOut: e.target.checked })} />
          Iesire unilaterala din negociere
        </label>
        {status.coolingEnd && <DeadlineChip label="Sfarsitul cooling-off-ului" date={status.coolingEnd} />}
        {r.coolingPrelungit && status.coolingMax && <DeadlineChip label="Limita maxima de prelungire" date={status.coolingMax} />}
      </TrackerCard>

      {/* Faza contradictorie */}
      <TrackerCard title="Faza contradictorie">
        <p className="f-hint mb8">Cand primesti calendarul de la EUIPO, trece datele exacte — ele au prioritate fata de calcul.</p>

        <div className="tr-divider">Probele oponentului</div>
        <div className="f-row">
          <div className="field">
            <label>Termen din calendarul EUIPO</label>
            <DateField value={r.termenSubstantiereManual} onChange={v => set({ termenSubstantiereManual: v })} />
            {status.termenSubstantiere && !r.termenSubstantiereManual && <span className="f-hint">Calculat: {fmtRO(status.termenSubstantiere)}</span>}
          </div>
          {status.termenSubstantiere && <DeadlineChip label="Termen probe oponent" date={status.termenSubstantiere} act={r.directie === 'formulata'} />}
        </div>
        <label className="check-label">
          <input type="checkbox" checked={r.substantiereDepusa} onChange={e => set({ substantiereDepusa: e.target.checked })} />
          Probele si argumentele au fost depuse
        </label>

        <div className="tr-divider mt8">Observatiile solicitantului</div>
        <div className="f-row">
          <div className="field">
            <label>Termen din calendarul EUIPO</label>
            <DateField value={r.termenObservatiiManual} onChange={v => set({ termenObservatiiManual: v })} />
            {status.termenObservatii && !r.termenObservatiiManual && <span className="f-hint">Calculat: {fmtRO(status.termenObservatii)}</span>}
          </div>
          {status.termenObservatii && <DeadlineChip label="Termen observatii" date={status.termenObservatii} act={r.directie === 'primita'} />}
        </div>
        <label className="check-label">
          <input type="checkbox" checked={r.observatiiDepuse} onChange={e => set({ observatiiDepuse: e.target.checked })} />
          Observatiile au fost depuse
        </label>

        <div className="tr-divider mt8">Dovada folosirii efective</div>
        <label className="check-label">
          <input type="checkbox" checked={r.dovadaCeruta} onChange={e => set({ dovadaCeruta: e.target.checked })} />
          S-a cerut dovada folosirii marcii anterioare
        </label>
        {r.dovadaCeruta && (
          <div className="dep-section">
            <div className="f-row">
              <div className="field">
                <label>Data cererii</label>
                <DateField value={r.dataCerereDovada} onChange={v => set({ dataCerereDovada: v })} />
              </div>
              <div className="field">
                <label>Termen din calendarul EUIPO</label>
                <DateField value={r.termenDovadaManual} onChange={v => set({ termenDovadaManual: v })} />
              </div>
            </div>
            {status.termenDovada && <DeadlineChip label="Termen depunere dovezi" date={status.termenDovada} act />}
            <label className="check-label">
              <input type="checkbox" checked={r.dovadaPrimita} onChange={e => set({ dovadaPrimita: e.target.checked })} />
              Dovezile au fost depuse
            </label>
          </div>
        )}

        <div className="tr-divider mt8">Altele</div>
        <div className="f-row">
          <div className="field">
            <label>Prelungiri acordate</label>
            <input type="number" min="0" value={r.prelungiri} onChange={e => set({ prelungiri: Math.max(0, Number(e.target.value)||0) })} className="mono" />
          </div>
          <div className="field">
            <label>Strategia adoptata</label>
            <FilterPill value={r.strategie} onChange={e => set({ strategie: e.target.value })}>
              {STRATEGII.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterPill>
          </div>
        </div>
        <label className="check-label">
          <input type="checkbox" checked={r.suspendata} onChange={e => set({ suspendata: e.target.checked })} />
          Procedura este suspendata
        </label>
      </TrackerCard>

      {/* Decizia */}
      <TrackerCard title="Decizia si calea de atac">
        <div className="f-row">
          <div className="field">
            <label>Solutia diviziei de opozitie</label>
            <FilterPill value={r.solutie} onChange={e => set({ solutie: e.target.value })}>
              {SOLUTII.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterPill>
          </div>
          <div className="field">
            <label>Data notificarii deciziei</label>
            <DateField value={r.dataDecizie} onChange={v => set({ dataDecizie: v })} />
            <span className="f-hint">Porneste cele doua termene de recurs.</span>
          </div>
        </div>
        {status.termenApelDeclaratie && (
          <>
            <DeadlineChip label={`Declaratia de recurs (${termene.apel_declaratie_luni} luni)`} date={status.termenApelDeclaratie} act={!r.apel} />
            <DeadlineChip label={`Memoriul de motivare (${termene.apel_motive_luni} luni)`} date={status.termenApelMotive} act={r.apel} />
          </>
        )}
        <div className="field mt8">
          <label>Cheltuielile de procedura</label>
          <FilterPill value={r.cheltuieliInSarcina} onChange={e => set({ cheltuieliInSarcina: e.target.value })}>
            <option value="">— nestabilite —</option>
            <option value="oponent">In sarcina oponentului</option>
            <option value="noi">In sarcina partii noastre</option>
            <option value="fiecare">Fiecare parte suporta propriile cheltuieli</option>
          </FilterPill>
        </div>
        <label className="check-label">
          <input type="checkbox" checked={r.apel} onChange={e => set({ apel: e.target.checked })} />
          S-a formulat recurs ({taxe.apel} €)
        </label>
        {r.apel && (
          <div className="field" style={{ marginLeft: 22 }}>
            <label>Data depunerii recursului</label>
            <DateField value={r.dataApel} onChange={v => set({ dataApel: v })} />
          </div>
        )}
        <label className={`check-label mt8${!r.solutie ? ' disabled' : ''}`}>
          <input type="checkbox" disabled={!r.solutie} checked={r.transmisClient} onChange={e => set({ transmisClient: e.target.checked })} />
          Clientul a fost informat despre solutie
        </label>
      </TrackerCard>

      {/* Taxe */}
      <TrackerCard title="Taxe si expunere la cheltuieli" badge="Anexa I RMUE">
        <table className="fee-table">
          <tbody>
            <FeeRow label="Taxa de opozitie" value={taxeCalc.opozitie} muted={r.directie === 'primita'} />
            {r.apel && <FeeRow label="Taxa de recurs" value={taxeCalc.apel} />}
          </tbody>
          <tfoot>
            <tr className="fee-total">
              <td>Taxe in sarcina noastra</td>
              <td>{fmtEur(taxeCalc.propriile)}</td>
            </tr>
          </tfoot>
        </table>
        <div className="mt8" style={{ background:'var(--bg-subtle)', border:'1px solid var(--border)', borderRadius: 6, padding: '8px 12px' }}>
          <p style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Daca pierdem</p>
          <FeeRow label="Plafon cheltuieli de reprezentare" value={taxe.plafon_opozitie} />
          {r.directie === 'primita' && <FeeRow label="Rambursarea taxei de opozitie" value={taxe.opozitie} />}
          <FeeRow label="Expunere maxima estimata" value={taxeCalc.expunere} strong />
        </div>
        <p className="f-hint mt4">In recurs, plafonul de cheltuieli de reprezentare urca la {taxe.plafon_apel} €.</p>

        <button type="button" className="btn-link mt8" onClick={() => setShowSetari(s => !s)}>
          {showSetari ? '▾' : '▸'} Tarife si termene (editabile)
        </button>
        {showSetari && (
          <div className="tarife-editor">
            <div className="tr-divider">Tarife (EUR)</div>
            {[['opozitie','Opozitie'],['apel','Recurs'],['anulare','Anulare / decadere'],['continuare','Continuare procedura'],
              ['restitutio','Repunere in drepturi'],['plafon_opozitie','Plafon chelt. opozitie'],['plafon_apel','Plafon chelt. apel']
            ].map(([k, l]) => (
              <label key={k} className="tarife-field">{l}
                <input type="number" value={taxe[k]} onChange={e => setTaxe({ ...taxe, [k]: Number(e.target.value)||0 })} />
              </label>
            ))}
            <div className="tr-divider">Termene (luni)</div>
            {[['opozitie_luni','Opozitie'],['ir_decalaj_luni','Decalaj IR'],['cooling_off_luni','Cooling-off initial'],
              ['cooling_off_max','Cooling-off maxim'],['substantiere_luni','Probe oponent'],['observatii_luni','Observatii'],
              ['dovada_luni','Dovada folosirii'],['apel_declaratie_luni','Declaratie recurs'],['apel_motive_luni','Memoriu recurs']
            ].map(([k, l]) => (
              <label key={k} className="tarife-field">{l}
                <input type="number" value={termene[k]} onChange={e => setTermene({ ...termene, [k]: Number(e.target.value)||0 })} />
              </label>
            ))}
          </div>
        )}
      </TrackerCard>

      <TrackerCard title="Observatii interne">
        <textarea className="tr-textarea" value={r.observatii} onChange={e => set({ observatii: e.target.value })}
          rows={3} placeholder="Argumentele oponentului, pozitia clientului, stadiul negocierii…" />
      </TrackerCard>
    </div>
  );
}

/* ── Sub-components ── */
function AlertBanner({ status }) {
  const { alert } = status;
  return (
    <div className={`tr-alert sev-${alert.sev}`}>
      <strong>{alert.label}</strong>
      {alert.date && (
        <span className="tr-alert-date">
          Termen: {fmtRO(alert.date)}
          {typeof alert.daysLeft === 'number' && (
            alert.daysLeft < 0
              ? ` · depasit cu ${Math.abs(alert.daysLeft)} zile`
              : ` · ${alert.daysLeft} zile ramase`
          )}
        </span>
      )}
    </div>
  );
}

function Timeline({ steps }) {
  const activeIdx = steps.findIndex(s => !s.done);
  return (
    <div className="tr-timeline">
      {steps.map((s, i) => {
        const isActive = i === activeIdx;
        const done = s.done;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className={`tl-line${done || steps[i-1].done ? ' tl-line-done' : ''}`} />}
            <div className="tl-step">
              <span className={`tl-dot${done ? ' done' : isActive ? ' active' : ''}`}>{done ? '✓' : i+1}</span>
              <span className={`tl-label${isActive ? ' active' : done ? ' done' : ''}`}>{s.label}</span>
              {s.date && <span className="tl-date">{fmtRO(s.date)}</span>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TrackerCard({ title, badge, children }) {
  return (
    <div className="tr-card">
      <div className="tr-card-head">
        <span className="tr-card-title">{title}</span>
        {badge && <span className="tr-badge">{badge}</span>}
      </div>
      <div className="tr-card-body">{children}</div>
    </div>
  );
}

function SegControl({ value, onChange, options }) {
  return (
    <div className="seg-ctrl">
      {options.map(([v, label]) => (
        <button key={v} type="button" className={`seg-btn${value === v ? ' seg-active' : ''}`}
          onClick={() => onChange(v)}>{label}</button>
      ))}
    </div>
  );
}

function FeeRow({ label, value, strong, muted }) {
  return (
    <tr className={strong ? 'fee-strong' : muted ? 'fee-muted' : ''}>
      <td>{label}</td>
      <td className="fee-val">{muted ? '' : fmtEur(value)}</td>
    </tr>
  );
}

function DeadlineChip({ label, date, act }) {
  if (!date) return null;
  const dl = daysLeft(date);
  const overdue = dl < 0;
  const soon = dl >= 0 && dl <= 14;
  const cls = overdue ? 'chip-overdue' : (act && soon) ? 'chip-act' : 'chip-neutral';
  return (
    <div className={`deadline-chip ${cls}`}>
      <span>{label}</span>
      <span>{fmtRO(date)} · {overdue ? `${Math.abs(dl)}z depasit` : `${dl}z`}</span>
    </div>
  );
}
